import { MessageService, ConfirmationService } from 'primeng/api';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { SchedulerEvent } from '../../../../models/SchedulerEvent';
import { AppointmentPeriod } from '../../../../models/AppointmentPeriod';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Calendar, CalendarOptions, EventClickArg } from '@fullcalendar/core';
import {
  FullCalendarComponent,
  FullCalendarModule
} from '@fullcalendar/angular';
import { User } from '../../../../models/User';
import { UserService } from '../../../../services/user.service';
import { AuthService } from '../../../../services/auth.service';
import { environment } from '../../../../../environments/environment';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/resource-timegrid';
import { UtilsService } from '../../../../services/utils.service';
import { Select } from 'primeng/select';
import { VisibleOnlyDirective } from '../../../../directives/visible-only.directive';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { SearchCriteria } from 'src/app/models/SearchCriteria';
import { AppointmentPeriodDialogComponent } from '../appointment-period-dialog/appointment-period-dialog.component';
import { AppointmentPeriodService } from 'src/app/services/appointment-period.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { OrganizationUnitService } from 'src/app/services/organization-unit.service';
import { UserAvailabilityService } from 'src/app/services/user-availability.service';
import { UserAvailability } from 'src/app/models/UserAvailability';
import { ScheduleService } from 'src/app/services/schedule.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PresenceBookingContext, PresenceBookingService } from 'src/app/services/presence-booking.service';


@Component({
  selector: 'app-appointment-period-schedule',
  imports: [
    Select,
    VisibleOnlyDirective,
    FormsModule,
    FullCalendarModule,
    TranslatePipe,
    ButtonDirective,
    Tooltip
  ],
  templateUrl: './appointment-period-schedule.component.html',
  styleUrls: ['./appointment-period-schedule.component.scss']
})
export class AppointmentPeriodScheduleComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() user: User | null = null;
  public presenceBooking: PresenceBookingContext | null = null;
  private returningToSchedule = false;
  public isMonthView = false;
  private calendarLicense = environment.fullCalendarLicenseKey;
  public searchCriteria: SearchCriteria;
  public calendarApi: Calendar | null = null;
  public appointmentPeriods: Array<AppointmentPeriod> | null = null;
  public events: Array<SchedulerEvent> | null = null;
  public loading = true;
  public users: Array<User> | null = null;
  public userResource: any;
  public userResources: Array<any> | null = null;
  public selectedUser: any;
  public viewSelections: Array<any> | null = null;
  private filterType: string = 'service';
  private userId: any;
  private readonly destroy$ = new Subject<void>();
  // Incremented on each load; responses from a superseded load are ignored.
  private loadToken = 0;

  public calendarOptions: CalendarOptions = {
    plugins: [
      interactionPlugin,
      resourceTimeGridPlugin,
      dayGridPlugin,
      timeGridPlugin
    ],
    schedulerLicenseKey: this.calendarLicense,
    timeZone: 'local',
    initialDate: new Date(),
    initialView: 'timeGridWeek',
    editable: true,
    selectable: true,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    slotDuration: '00:30:00',
    resourceOrder: 'sortOrder',
    resources: [],
    customButtons: {
      backCustom: {
        text: '<',
        click: () => {
          this.setDate('prev');
        }
      },
      todayCustom: {
        text: 'today',
        click: () => {
          this.setDate('today');
        }
      },
      nextCustom: {
        text: '>',
        click: () => {
          this.setDate('next');
        }
      }
    },
    // headerToolbar: false,
    headerToolbar: {
      left: '',
      center: 'title',
      right: 'backCustom,todayCustom,nextCustom'
    },
    firstDay: 1,
    allDaySlot: false,
    select: this.handleEventSelect.bind(this),
    eventClick: this.handleDateClick.bind(this),
    eventChange: this.handleEventChange.bind(this),
    eventsSet: this.handleEvents.bind(this)
  };

  @ViewChild('appointmentPeriodProfile') calendarComponent:
    | FullCalendarComponent
    | undefined;

  private setDate(direction: string): void {
    switch (direction) {
      case 'month':
        this.calendarApi?.changeView('dayGridMonth');
        // Keep the searched date in sync with the currently viewed date so
        // the view type and date stay consistent when switching Month/Week/Day.
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'week':
        this.calendarApi?.changeView('timeGridWeek');
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'day':
        this.calendarApi?.changeView('timeGridDay');
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'prev':
        this.calendarApi?.prev();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'today':
        this.calendarApi?.today();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
      case 'next':
        this.calendarApi?.next();
        this.searchCriteria.startDate =
          this.calendarApi?.getDate() || new Date();
        break;
    }
    this.getEvents();
  }

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    private messageService: MessageService,
    private userService: UserService,
    private authService: AuthService,
    private appointmentPeriodService: AppointmentPeriodService,
    private organizationUnitService: OrganizationUnitService,
    private availabilityService: UserAvailabilityService,
    private scheduleService: ScheduleService,
    private presenceBookingService: PresenceBookingService,
    private router: Router
  ) {
    this.searchCriteria = {} as SearchCriteria;
    this.searchCriteria.startDate = new Date();
    this.userResource = {
      id: 'USERS',
      title: 'event.resources.user-selector-title',
      filterTitle: 'event.resources.user-filter-title'
    };
  }

  ngOnInit(): void {
    this.calendarOptions.locale =
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang() ||
      'fi';
    // Default to the currently logged-in user when no user is provided.
    if (!this.user) {
      this.user = this.authService.getUserProfile();
    }
    this.userId = this.user?.id;
    // this.getOrganizationUnit$().subscribe(() => {});
    this.loadTranslations();
    this.presenceBooking = this.presenceBookingService.getContext();

    // Localize the Month/Week/Day view buttons.
    const cb = this.calendarOptions.customButtons;
    if (cb) {
      if (cb.todayCustom) {
        cb.todayCustom.text =
          this.translateService.instant('appointment-period.today');
      }
    }

    const organizationUnit = this.user?.activeOrganizationUnit;
    if (organizationUnit) {
      if (organizationUnit.scheduleEventDuration) {
        this.calendarOptions.slotDuration =
          organizationUnit.scheduleEventDuration;
      }
      if (organizationUnit.scheduleStart) {
        this.calendarOptions.slotMinTime = organizationUnit.scheduleStart;
      }
      if (organizationUnit.scheduleEnd) {
        this.calendarOptions.slotMaxTime = organizationUnit.scheduleEnd;
      }
    }

    /*
    this.userService.getScheduleUsers().subscribe((result) => {
      this.users = result;
      this.userResources = this.users!.map((user) => {
        return {
          id: user.id,
          title: `${user.lastName} ${user.firstName}`,
          sortOrder: `${user.lastName}`
        };
      });

      // Set the default selected user
      this.selectedUser = this.userResources.find(
        (user) => user.id === this.userId
      );
    });
    */
  }

  resourceFilterChange(event: any, type: string): void {
    if (this.authService.isPermission('update_all_users_availability')) {
      this.userId = this.selectedUser.id;
      this.getEvents();
    }
  }

  ngAfterViewInit(): void {
    this.ensureCalendarApi();
    // calendarApi is available here so request events using the calendar view range
    this.getEvents();
  }

  refreshCalendar(): void {
    this.ensureCalendarApi();
    this.calendarApi?.setOption('contentHeight', 650);
    this.calendarApi?.changeView('timeGridWeek');
    // The calendar may have been initialised while its tab/section was hidden
    // (0 size). Force a re-layout now that it is visible.
    this.calendarApi?.updateSize();
    this.getEvents();
  }

  // Resolve the FullCalendar API (may be null when first created in a hidden tab).
  private ensureCalendarApi(): boolean {
    if (!this.calendarApi && this.calendarComponent) {
      this.calendarApi = this.calendarComponent.getApi();
    }
    return !!this.calendarApi;
  }

  public getEvents(): void {
    this.appointmentPeriods = [];
    this.events = [];

    // Without a resolved calendar (hidden tab) the view range is undefined;
    // skip now and let refreshCalendar() reload once the calendar is visible.
    if (!this.ensureCalendarApi()) {
      return;
    }
    this.calendarApi?.removeAllEvents();

    if (this.user) {
      const token = ++this.loadToken;

      const startDate = this.calendarApi?.view.currentStart;
      const endDate = this.calendarApi?.view.currentEnd;
      if (!startDate || !endDate) {
        this.loading = false;
        return;
      }

      const userPeriod = {
        userId: this.userId,
        startDate,
        endDate
      };

      this.loadAvailabilities(token);

      this.appointmentPeriodService
        .getAppointmentPeriodByDates(userPeriod)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            if (token !== this.loadToken) {
              return;
            }
            if (res.length === 0) {
              this.loading = false;
            } else {
              this.appointmentPeriods = res;
              this.mapPeriodToCalendarEvents(res, token);
            }
          },
          () => {
            if (token !== this.loadToken) {
              return;
            }
            this.loading = false;
          }
        );

      const sc = {
        startDate
      };
      this.availabilityService.getAvailableUsersFull(sc).subscribe((result) => {
        if (result && result.length > 0) {
          this.users = result;
          this.userResources = this.users!.map((user) => {
            return {
              id: user.id,
              title: `${user.lastName} ${user.firstName}`,
              sortOrder: `${user.lastName}`
            };
          });

          // Set the default selected user
          this.selectedUser = this.userResources.find(
            (user) => user.id === this.userId
          );
        } else {
          this.getScheduleUsers();
        }
      }, error => {
        this.getScheduleUsers();
      });
    }
  }

  private getScheduleUsers(): void {
    this.userService.getScheduleUsers().subscribe((result) => {
      this.users = result;
      this.userResources = this.users!.map((user) => {
        return {
          id: user.id,
          title: `${user.lastName} ${user.firstName}`,
          sortOrder: `${user.lastName}`
        };
      });

      // Set the default selected user
      this.selectedUser = this.userResources.find(
        (user) => user.id === this.userId
      );
    });
  }

  /**
   * Load all translations
   */
  private loadTranslations(): void {
    this.translateService
      .get([
        'event.messages.event-remove-confirmation',
        'event.messages.event-save-success',
        'event.messages.event-delete-success',
        'event.messages.no-events-found',
        'event.btn-today',
        'event.btn-this-week',
        'event.btn-this-month'
      ])
      .subscribe((result) => {
        this.viewSelections = [
          {
            name: result['event.btn-today'],
            code: 0
          },
          {
            name: result['event.btn-this-week'],
            code: 1
          },
          {
            name: result['event.btn-this-month'],
            code: 2
          }
        ];
      });
  }

  // Fetch the selected user's availabilities for the current calendar view range.
  private loadAvailabilities(token: number): void {
    const searchCriteria = {} as SearchCriteria;
    searchCriteria.startDate = this.calendarApi?.view.currentStart;
    searchCriteria.endDate = this.calendarApi?.view.currentEnd;
    searchCriteria.userId = this.userId;
    if (!searchCriteria.startDate || !searchCriteria.endDate) {
      return;
    }
    this.availabilityService
      .getUserAvailabilityByDates(searchCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          if (token !== this.loadToken) {
            return;
          }
          this.mapAvailabilityToCalendarEvents(result, token);
        },
        () => {
          if (token !== this.loadToken) {
            return;
          }
          this.loading = false;
        }
      );
  }

  // Render availabilities as background events behind the appointment periods.
  private mapAvailabilityToCalendarEvents(
    availabilities: Array<UserAvailability>,
    token: number
  ): void {
    if (!availabilities || token !== this.loadToken) {
      return;
    }
    availabilities.forEach((availability) => {
      this.calendarApi!.getCurrentData().calendarApi.addEvent({
        start: availability.availabilityStart,
        end: availability.availabilityEnd,
        display: 'background',
        backgroundColor: this.scheduleService.getEventColor(
          availability.type || 'default'
        ),
        event: availability
      });
    });
  }

  private mapPeriodToCalendarEvents(
    appointmentPeriods: Array<any>,
    token: number
  ): void {
    if (token !== this.loadToken) {
      return;
    }
    appointmentPeriods.forEach((period) => {
      this.expandPeriodToEvents(period).forEach((e) => {
        this.calendarApi!.getCurrentData().calendarApi.addEvent(e);
      });
    });
    this.loading = false;
  }

  // Combine a date-only value (Date or ISO string) with an 'HH:MM' time string.
  private combineDateTime(date: any, time: any): Date | undefined {
    if (!date) {
      return undefined;
    }
    const base = date instanceof Date ? new Date(date) : new Date(date);
    if (isNaN(base.getTime())) {
      return undefined;
    }
    if (typeof time === 'string' && /^\d{1,2}:\d{2}/.test(time)) {
      const [hours, minutes] = time.split(':').map(Number);
      base.setHours(hours, minutes, 0, 0);
    }
    return base;
  }

  private periodToEvent(period: any): void {
    // Adds an AppointmentPeriod as one calendar event per matching day.
    this.expandPeriodToEvents(period).forEach((e) => {
      this.calendarApi!.getCurrentData().calendarApi.addEvent(e);
    });
  }

  // Expand a (possibly multi-day) period into one event per day in its
  // start..end range whose weekday is in weekdays (all days if unset).
  private expandPeriodToEvents(period: any): Array<any> {
    const eventBg = '#E6F1ED';
    const start = this.dateOnly(period.startDate);
    const end = this.dateOnly(period.endDate) || start;
    if (!start || !end) {
      return [];
    }
    const allowedDays = this.parseWeekDays(period);
    const events: Array<any> = [];
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard++ < 366) {
      if (!allowedDays || allowedDays.has(cursor.getDay())) {
        events.push({
          id: `${period.id}::${cursor.getFullYear()}-${cursor.getMonth() + 1}-${cursor.getDate()}`,
          start: this.combineDateTime(cursor, period.startTime),
          end: this.combineDateTime(cursor, period.endTime),
          title: period.info || period.speciality || '',
          event: period,
          backgroundColor: eventBg,
          borderColor: eventBg
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return events;
  }

  private parseWeekDays(period: any): Set<number> | null {
    if (Array.isArray(period.days) && period.days.length) {
      return new Set<number>(period.days.map(Number));
    }
    if (typeof period.weekdays === 'string' && period.weekdays.trim()) {
      const set = new Set<number>(
        period.weekdays
          .split(',')
          .map((day: string) => Number(day.trim()))
          .filter((day: number) => !isNaN(day))
      );
      return set.size ? set : null;
    }
    return null;
  }

  private dateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? new Date(value) : new Date(value);
    return isNaN(date.getTime())
      ? null
      : new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private removeEventsForPeriod(periodId: string): void {
    this.calendarApi
      ?.getEvents()
      .filter((e) => e.extendedProps?.['event']?.id === periodId)
      .forEach((e) => e.remove());
  }

  public handleEventSelect(info: any): void {
    // Selecting empty calendar space opens the enhanced date-range/breaks dialog,
    // prefilled to the selected day and time. Editing an existing block still
    // uses the single-period dialog (handleDateClick).
    this.openBulkDialog('day', info);
  }

  public handleDateClick(info: EventClickArg): void {
    const profileObject = info.event.extendedProps.event;
    const data = {
      info,
      profileObject,
      filterType: this.filterType
    };

    this.handleDialog(data);
  }

  public handleDialog(data: any): void {
    const ref = this.dialogService.open(AppointmentPeriodDialogComponent, {
      header: this.translateService.instant('appointment-period.edit-period'),
      data,
      width: '520px',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: any) => {
      if (retValue) {
        if (retValue.type === 'newPeriod' && retValue.data) {
          this.appointmentPeriods?.push(retValue.data);
          this.periodToEvent(retValue.data);
        } else if (retValue.type === 'updatePeriod') {
          const delIndex = this.appointmentPeriods?.findIndex(
            (a) => a.id === retValue.data.id
          );
          if (
            (delIndex || delIndex === 0) &&
            delIndex !== -1 &&
            this.appointmentPeriods
          ) {
            this.appointmentPeriods.splice(delIndex, 1, retValue.data);
          }
          this.removeEventsForPeriod(retValue.data.id);
          this.periodToEvent(retValue.data);
        } else if (
          retValue.type === 'delPeriod' &&
          retValue.data &&
          retValue.data.id
        ) {
          const delIndex = this.appointmentPeriods?.findIndex(
            (a) => a.id === retValue.data.id
          );
          if (
            (delIndex || delIndex === 0) &&
            delIndex !== -1 &&
            this.appointmentPeriods
          ) {
            this.appointmentPeriods.splice(delIndex, 1);
          }
          this.removeEventsForPeriod(retValue.data.id);
        }
      }
    });
  }

  /**
   * Open the enhanced "add presence" dialog.
   *  - 'range' (toolbar button): prefill the currently visible week/day range,
   *    Mon-Fri, using the org-unit slot times.
   *  - 'day' (calendar drag-select): prefill a single day + the selected times.
   */
  public openBulkDialog(mode: 'range' | 'day', info?: any): void {
    const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    const hhmm = (d: Date): string =>
      `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const data: any = { userId: this.userId, filterType: this.filterType };

    if (mode === 'day' && info?.start) {
      const start: Date = info.start;
      let end: Date | null = info.end || null;
      let endTime: string | null = end && end > start ? hhmm(end) : null;
      // A selection ending at midnight is exclusive: it ends at the end of
      // the previous day, not at 00:00 on the next one.
      if (end && endTime === '00:00') {
        end = new Date(end);
        end.setDate(end.getDate() - 1);
        endTime = '23:59';
      }
      data.defaultStartDate = start;
      data.defaultEndDate = end;
      data.onlyJsDay = start.getDay();
      data.defaultStartTime = hhmm(start);
      if (endTime) {
        data.defaultEndTime = endTime;
      }
    } else {
      const viewStart = this.calendarApi?.view.currentStart || new Date();
      const viewEnd = this.calendarApi?.view.currentEnd; // exclusive
      data.defaultStartDate = viewStart;
      if (viewEnd) {
        const inclusiveEnd = new Date(viewEnd);
        inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
        data.defaultEndDate = inclusiveEnd;
      } else {
        data.defaultEndDate = viewStart;
      }
      data.defaultStartTime =
        this.toHhmm(this.calendarOptions.slotMinTime) || '08:00';
      data.defaultEndTime =
        this.toHhmm(this.calendarOptions.slotMaxTime) || '16:00';
    }

    const ref = this.dialogService.open(AppointmentPeriodDialogComponent, {
      header: this.translateService.instant('appointment-period.add-period'),
      data,
      width: '640px',
      closable: true,
      draggable: true,
      resizable: true,
      baseZIndex: 10000
    });

    ref!.onClose.subscribe((retValue: any) => {
      if (retValue && retValue.type === 'batchAdded') {
        const msg = this.translateService.instant('appointment-period.save-summary', {
          added: retValue.added,
          replaced: retValue.replaced
        });
        this.messageService.add({
          severity: 'success',
          summary: '',
          detail: msg
        });
        this.getEvents();
      }
    });
  }

  public returnToSchedule(): void {
    if (this.presenceBooking?.returnUrl) {
      this.returningToSchedule = true;
      this.router.navigateByUrl(this.presenceBooking.returnUrl);
    }
  }

  ngOnDestroy(): void {
    if (!this.returningToSchedule) {
      this.presenceBookingService.clearContext();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toHhmm(value: any): string | null {
    if (!value || typeof value !== 'string') {
      return null;
    }
    const parts = value.split(':');
    if (parts.length < 2) {
      return null;
    }
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }

  public handleEventChange(info: any): void {
    const profileObject = info.event.extendedProps.event;
    const start: Date | null = info.event.start;
    const end: Date | null = info.event.end;

    // A calendar event is a single day of the period; a drag/resize only
    // changes the daily start/end time, keeping the period's date range.
    const updatedProfile: AppointmentPeriod = {
      ...profileObject,
      startTime: start ? this.hhmm(start) : profileObject.startTime,
      endTime: end ? this.hhmm(end) : profileObject.endTime
    };

    this.appointmentPeriodService
      .saveAppointmentPeriod(updatedProfile)
      .subscribe((res) => {
        const saved = res || updatedProfile;
        const index = this.appointmentPeriods?.findIndex(
          (a) => a.id === saved.id
        );
        if (index !== undefined && index !== -1 && this.appointmentPeriods) {
          this.appointmentPeriods.splice(index, 1, saved);
        }
        this.removeEventsForPeriod(saved.id);
        this.periodToEvent(saved);
      });
  }

  private hhmm(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}`;
  }

  private handleEvents(info: any): void {
    // Copy is only meaningful in day/week view, so hide it in month view.
    this.isMonthView = this.calendarApi?.view.type === 'dayGridMonth';
  }
}
